import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

// Generic shape — the endpoint isn't STP-specific. Anything that
// upserts into the `incidents` table with a device_id appears here.
// Fields match `views::topology_findings` on the backend exactly.

export interface TopologyFindingItem {
  incidentId: string;
  deviceId: string;
  severity: string;
  title: string;
  description: string | null;
  dedupeKey: string | null;
  taskId: string | null;
  source: string;
  /**
   * Future-positioned. v1 always null because `incidents` doesn't
   * carry an interface column yet. When a future migration adds
   * it, this field starts driving per-edge badge attribution
   * without any contract change.
   */
  interfaceId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TopologyFindingsResponse {
  regionId: string;
  findings: TopologyFindingItem[];
}

/**
 * Fetch active incidents for every device in the region.
 *
 * Always-on overlay — runs whenever a region is selected, not gated
 * to a specific layer. The findings badge primitive sits above
 * whatever layer the user is currently viewing.
 */
export function useTopologyFindings(regionId: string | null | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<TopologyFindingsResponse>({
    queryKey: ['topologyFindings', regionId],
    queryFn: async () => {
      if (!regionId) throw new Error('Region ID is required');
      const response = await axios.get<TopologyFindingsResponse>(
        `${config.backendApi}/api/topology-findings`,
        {
          params: { regionId },
          withCredentials: true,
        }
      );
      return response.data;
    },
    enabled: !!regionId && contextReady,
  });
}

/**
 * Severity → integer rank for "worst-wins" aggregation. Lower = worse.
 *
 * The canonical order, which also fixes an inversion this used to carry:
 * `info` ranked above `low`.
 */
export function severityRank(severity: string): number {
  switch (severity.toLowerCase()) {
    case 'critical': return 0;
    case 'high': return 1;
    case 'medium': return 2;
    case 'low': return 3;
    case 'info': return 4;
    default: return 5;
  }
}

/** Color for the severity badge background. */
export function severityColor(severity: string): string {
  switch (severity.toLowerCase()) {
    case 'critical':
    case 'high':
      return '#f14668'; // bulma danger
    case 'medium':
      return '#ffdd57'; // bulma warning
    case 'info':
      return '#3273dc'; // bulma info
    case 'low':
      return '#b5b5b5';
    default:
      return '#b5b5b5';
  }
}

/** Whether dark text reads better on the severity color background. */
export function severityNeedsDarkText(severity: string): boolean {
  // The mid-scale yellow is the only severity colour bright enough to want dark text.
  return severity.toLowerCase() === 'medium';
}

/**
 * Aggregate flat findings into per-device summaries.
 * Returns a Map<deviceId, { count, worstSeverity, findings }>.
 */
export interface DeviceFindingSummary {
  deviceId: string;
  count: number;
  worstSeverity: string;
  /** All findings for this device, sorted worst-first. Used for hover. */
  findings: TopologyFindingItem[];
}

export function summarizeFindingsByDevice(
  findings: TopologyFindingItem[]
): Map<string, DeviceFindingSummary> {
  const byDevice = new Map<string, DeviceFindingSummary>();
  for (const f of findings) {
    const existing = byDevice.get(f.deviceId);
    if (existing) {
      existing.count += 1;
      existing.findings.push(f);
      if (severityRank(f.severity) < severityRank(existing.worstSeverity)) {
        existing.worstSeverity = f.severity;
      }
    } else {
      byDevice.set(f.deviceId, {
        deviceId: f.deviceId,
        count: 1,
        worstSeverity: f.severity,
        findings: [f],
      });
    }
  }
  // Sort each device's findings worst-first so the hover tooltip
  // and any "show top N" reductions take the most actionable
  // findings first.
  for (const summary of byDevice.values()) {
    summary.findings.sort((a, b) => severityRank(a.severity) - severityRank(b.severity));
  }
  return byDevice;
}
